import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = '/api';

export default function ExportModal({ filtered, onClose }) {
  const [opciones, setOpciones] = useState({
    resumen: true,
    sesiones: true,
    lecturas: false,
  });
  const [exportando, setExportando] = useState(false);
  const [progreso, setProgreso] = useState('');

  const toggle = key => setOpciones(o => ({ ...o, [key]: !o[key] }));

  const estimarTamano = () => {
    let kb = 0;
    if (opciones.resumen) kb += 20;
    if (opciones.sesiones) kb += filtered.length * 0.2;
    if (opciones.lecturas) kb += filtered.length * 15; // ~15kb por sesión
    return kb > 1024 ? `~${(kb/1024).toFixed(1)} MB` : `~${Math.round(kb)} KB`;
  };

  const estimarTiempo = () => {
    if (opciones.lecturas && filtered.length > 100) return `~${Math.round(filtered.length * 0.3)} seg`;
    if (opciones.lecturas) return '~15 seg';
    return '< 2 seg';
  };

  const exportar = async () => {
    if (!opciones.resumen && !opciones.sesiones && !opciones.lecturas) return;
    setExportando(true);

    try {
      const wb = XLSX.utils.book_new();
      const fmt = ts => ts ? new Date(ts).toLocaleString('es-CO') : '—';
      const pad = n => String(n).padStart(2,'0');
      const dur = m => m>0?(m>=60?`${Math.floor(m/60)}h ${pad(m%60)}m`:`${m}m`):'—';

      // ── HOJA 1: RESUMEN ──
      if (opciones.resumen) {
        setProgreso('Generando resumen...');
        const porCargador = {};
        const porBus = {};
        filtered.forEach(t => {
          if (!porCargador[t.cpId]) porCargador[t.cpId] = { sesiones:0, energia:0, cop:t.cop };
          porCargador[t.cpId].sesiones++;
          porCargador[t.cpId].energia += t.energyDelivered||0;
          const key = t.zonal || t.idTag;
          if (!porBus[key]) porBus[key] = { sesiones:0, energia:0, vin:t.vin, cop:t.cop, socFins:[], duraciones:[] };
          porBus[key].sesiones++;
          porBus[key].energia += t.energyDelivered||0;
          if (t.socEnd > 0) porBus[key].socFins.push(t.socEnd);
          if (t.duracion > 0) porBus[key].duraciones.push(t.duracion);
        });

        const data = [
          ['REPORTE GENERAL DE FLOTA — CELSIA — BLANCO Y NEGRO'],
          ['Generado:', new Date().toLocaleString('es-CO')],
          ['Total sesiones:', filtered.length],
          ['Energía total (kWh):', filtered.reduce((a,t)=>a+(t.energyDelivered||0),0)],
          ['Cargadores:', Object.keys(porCargador).length],
          ['Buses identificados:', Object.keys(porBus).length],
          [],
          ['── POR CARGADOR ──'],
          ['Cargador','COP','Sesiones','Energía (kWh)','Prom. kWh/sesión'],
          ...Object.entries(porCargador).map(([id,d]) => [id, d.cop, d.sesiones, d.energia, Math.round(d.energia/d.sesiones)]),
          [],
          ['── POR BUS / ZONAL ──'],
          ['Zonal','VIN','COP','Sesiones','Energía (kWh)','SoC final prom.','Duración prom.'],
          ...Object.entries(porBus).sort((a,b)=>b[1].energia-a[1].energia).map(([zonal,d]) => [
            zonal, d.vin||'—', d.cop, d.sesiones, d.energia,
            d.socFins.length>0 ? Math.round(d.socFins.reduce((a,b)=>a+b,0)/d.socFins.length)+'%' : '—',
            d.duraciones.length>0 ? dur(Math.round(d.duraciones.reduce((a,b)=>a+b,0)/d.duraciones.length)) : '—',
          ]),
        ];
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{wch:15},{wch:12},{wch:10},{wch:15},{wch:16},{wch:14},{wch:14}];
        XLSX.utils.book_append_sheet(wb, ws, 'Resumen');
      }

      // ── HOJA 2: SESIONES ──
      if (opciones.sesiones) {
        setProgreso('Generando hoja de sesiones...');
        const MAX = 10000;
        const data = [
          ['ID Tx','Zonal','VIN','Cargador','Pistola','COP','Inicio','Fin','Duración','SoC inicio (%)','SoC fin (%)','Energía (kWh)','Estado'],
          ...filtered.slice(0, MAX).map(t => [
            t.txId, t.zonal||'—', t.vin||'—', t.cpId, `P${t.connectorId}`, t.cop,
            fmt(t.startTime), fmt(t.stopTime), dur(t.duracion),
            t.socStart, t.socEnd, t.energyDelivered,
            t.status==='Completed'?'Completada':t.status==='Active'?'En carga':'Error'
          ])
        ];
        if (filtered.length > MAX) data.push([`⚠ Limitado a ${MAX} sesiones de ${filtered.length} totales`]);
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{wch:10},{wch:12},{wch:20},{wch:10},{wch:8},{wch:10},{wch:22},{wch:22},{wch:10},{wch:12},{wch:10},{wch:12},{wch:12}];
        XLSX.utils.book_append_sheet(wb, ws, 'Sesiones');
      }

      // ── HOJA 3: LECTURAS MINUTO A MINUTO ──
      if (opciones.lecturas) {
        const MAX_TX = 200;
        const txIds = filtered.slice(0, MAX_TX).map(t => t.txId);
        const meterData = [['Timestamp','Zonal','VIN','Cargador','Pistola','SoC (%)','Potencia (kW)','Voltaje (V)','Corriente (A)','Energía (Wh)']];

        for (let i = 0; i < txIds.length; i++) {
          const txId = txIds[i];
          const tx = filtered.find(t => t.txId === txId);
          setProgreso(`Lecturas: sesión ${i+1} de ${txIds.length}...`);
          try {
            const resp = await fetch(`${API}/metervalues?txId=${txId}`);
            const lecturas = await resp.json();
            lecturas.forEach(r => {
              meterData.push([
                r.ts, tx?.zonal||'—', tx?.vin||'—', tx?.cpId||'—', `P${r.connector_id||1}`,
                r.soc, r.power, r.voltage, r.current, r.energy
              ]);
            });
          } catch(e) {}
        }

        if (filtered.length > MAX_TX) meterData.push([`⚠ Limitado a ${MAX_TX} sesiones de ${filtered.length} totales`]);
        const ws = XLSX.utils.aoa_to_sheet(meterData);
        ws['!cols'] = [{wch:22},{wch:12},{wch:20},{wch:10},{wch:8},{wch:8},{wch:12},{wch:10},{wch:12},{wch:14}];
        XLSX.utils.book_append_sheet(wb, ws, 'Lecturas');
      }

      setProgreso('Guardando archivo...');
      const buf = XLSX.write(wb, { bookType:'xlsx', type:'array' });
      const fecha = new Date().toISOString().slice(0,10);
      saveAs(new Blob([buf], {type:'application/octet-stream'}), `CELSIA_Reporte_${fecha}.xlsx`);
      onClose();
    } catch(e) {
      console.error('Error exportando:', e);
      setProgreso('Error al exportar. Intenta de nuevo.');
    }
    setExportando(false);
  };

  const nada = !opciones.resumen && !opciones.sesiones && !opciones.lecturas;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
      <div style={{background:'#13151f',border:'1px solid #2a2d3a',borderRadius:'12px',width:'100%',maxWidth:'460px',padding:'1.5rem'}}>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
          <div>
            <div style={{fontSize:'16px',fontWeight:'600',color:'#fff'}}>⬇ Exportar Excel</div>
            <div style={{fontSize:'12px',color:'#666',marginTop:'3px'}}>{filtered.length} sesiones en el rango seleccionado</div>
          </div>
          <button onClick={onClose} style={{padding:'5px 10px',background:'#2a2d3a',border:'none',borderRadius:'6px',color:'#fff',cursor:'pointer'}}>✕</button>
        </div>

        {/* Opciones */}
        <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'1.5rem'}}>
          {[
            { key:'resumen', label:'📊 Hoja Resumen', desc:'Totales por cargador y por bus/zonal', size:'~20 KB', time:'< 1 seg' },
            { key:'sesiones', label:'📋 Hoja Sesiones', desc:`Todas las sesiones con SoC y energía (máx. 10,000)`, size:`~${Math.round(filtered.length*0.2)} KB`, time:'< 2 seg' },
            { key:'lecturas', label:'📈 Lecturas minuto a minuto', desc:`Voltaje, corriente, potencia por sesión (máx. 200 sesiones)`, size:`~${Math.min(filtered.length,200)*15} KB`, time: filtered.length>50?`~${Math.round(Math.min(filtered.length,200)*0.3)} seg`:'~10 seg' },
          ].map(op => (
            <div key={op.key} onClick={()=>toggle(op.key)}
              style={{display:'flex',alignItems:'flex-start',gap:'12px',padding:'12px',borderRadius:'8px',cursor:'pointer',
                background: opciones[op.key]?'#1a2a1a':'#1a1d27',
                border:`1px solid ${opciones[op.key]?'#1d6a3a':'#2a2d3a'}`}}>
              <div style={{width:'20px',height:'20px',borderRadius:'5px',border:`2px solid ${opciones[op.key]?'#4ade80':'#2a2d3a'}`,
                background:opciones[op.key]?'#4ade80':'transparent',display:'flex',alignItems:'center',justifyContent:'center',
                color:'#000',fontSize:'13px',fontWeight:'700',flexShrink:0,marginTop:'1px'}}>
                {opciones[op.key]?'✓':''}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:'13px',fontWeight:'500',color:opciones[op.key]?'#fff':'#aaa'}}>{op.label}</div>
                <div style={{fontSize:'11px',color:'#555',marginTop:'2px'}}>{op.desc}</div>
              </div>
              <div style={{textAlign:'right',fontSize:'11px',color:'#555',flexShrink:0}}>
                <div>{op.size}</div>
                <div>{op.time}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Estimado total */}
        <div style={{background:'#1a1d27',border:'1px solid #2a2d3a',borderRadius:'8px',padding:'10px 14px',marginBottom:'1.25rem',display:'flex',justifyContent:'space-between',fontSize:'13px'}}>
          <span style={{color:'#aaa'}}>Tamaño estimado</span>
          <span style={{color:'#fff',fontWeight:'500'}}>{estimarTamano()} · {estimarTiempo()}</span>
        </div>

        {/* Progreso */}
        {exportando && (
          <div style={{background:'#1a2a1a',border:'1px solid #1d6a3a',borderRadius:'8px',padding:'10px 14px',marginBottom:'1.25rem',fontSize:'13px',color:'#4ade80'}}>
            ⏳ {progreso}
          </div>
        )}

        {/* Botones */}
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={onClose} disabled={exportando}
            style={{flex:1,padding:'10px',background:'#2a2d3a',border:'none',borderRadius:'7px',color:'#aaa',cursor:'pointer',fontSize:'13px'}}>
            Cancelar
          </button>
          <button onClick={exportar} disabled={exportando||nada}
            style={{flex:2,padding:'10px',background:nada?'#2a2d3a':'#1d6a3a',border:'none',borderRadius:'7px',
              color:nada?'#555':'#fff',cursor:nada||exportando?'default':'pointer',fontSize:'13px',fontWeight:'600'}}>
            {exportando ? '⏳ Exportando...' : '⬇ Exportar'}
          </button>
        </div>
      </div>
    </div>
  );
}
